// src/pages/Support/SupportOrders.tsx - COMPLETE FIXED VERSION
import React, { useState, useEffect } from 'react';
import { supportAPI } from '../../api';
import { Search, Eye, MapPin } from 'lucide-react';
import { formatCurrency, formatDate, getStatusColor } from '../../utils';
import { formatDisplayId } from '../../utils/idGenerator';
import { Link } from 'react-router-dom';

interface Order {
  _id: string;
  orderId: string;
  customerId: { fullName: string; email: string; _id: string };
  riderId: { fullName: string; email: string; _id: string };
  serviceType: 'ride' | 'delivery';
  pickupLocation: string;
  dropoffLocation: string;
  fixedPrice: number;
  status: string;
  createdAt: string;
}

const SupportOrders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 20;

  useEffect(() => {
    fetchOrders();
  }, [page, statusFilter]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await supportAPI.getAllOrders({ 
        page, 
        limit,
        status: statusFilter || undefined
      });
      if (res.data.success && res.data.data) {
        const ordersData = res.data.data.orders || [];
        setOrders(ordersData as Order[]);
        setTotalPages(res.data.data.pagination?.pages || 1);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter(order => 
    formatDisplayId(order.orderId).toLowerCase().includes(search.toLowerCase()) ||
    order.customerId?.fullName?.toLowerCase().includes(search.toLowerCase()) ||
    order.riderId?.fullName?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Orders</h1>
      </div>

      <div className="bg-white rounded-2xl p-4 shadow-lg mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by Order ID, Customer, or Rider..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg"
              />
            </div>
          </div>
          <div className="w-40">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="expired">Expired</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">Loading...</div>
      ) : (
        <>
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Order ID</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Customer</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Rider</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Route</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Amount</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Date</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredOrders.map((order) => (
                    <tr key={order._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-mono text-sm">
                        <span title={order.orderId}>{formatDisplayId(order.orderId)}</span>
                      </td>
                      <td className="px-6 py-4">{order.customerId?.fullName || 'N/A'}</td>
                      <td className="px-6 py-4">{order.riderId?.fullName || 'N/A'}</td>
                      <td className="px-6 py-4 max-w-xs">
                        <div className="flex items-center gap-1 text-xs">
                          <MapPin className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{order.pickupLocation} → {order.dropoffLocation}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 font-bold">{formatCurrency(order.fixedPrice)}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">{formatDate(order.createdAt)}</td>
                      <td className="px-6 py-4">
                        <Link
                          to={`/order/${order._id}`}
                          target="_blank"
                          className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg inline-flex"
                          title="View Order"
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-center mt-6 gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 border rounded-lg disabled:opacity-50"
            >
              Previous
            </button>
            <span className="px-4 py-2">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 border rounded-lg disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default SupportOrders;